/**
 * <s-theme-tip>: a cursor tooltip for the theme cards in "Meet the themes".
 * Each card ([data-tip-tone="premium|essential"]) carries its explanation in a
 * hidden [data-tip] (what it is, best-for niche, choose-it-if, proof chips and
 * where a click goes). On mouse hover the content is copied into one floating
 * panel in <body> (position: fixed, so it is never clipped by the gallery) that
 * follows the pointer and flips at the viewport edges. Keyboard focus shows it
 * beside the focused card. Clicks always go to the card's link.
 * Off on touch devices.
 */
class SThemeTip extends HTMLElement {
  #scope = null;
  #panel = null;
  #card = null;
  #point = null;
  #frame = 0;

  #onOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const card = event.target.closest?.('[data-tip-tone]');
    if (!card || !this.#scope.contains(card) || card === this.#card) return;
    this.#point = { x: event.clientX, y: event.clientY };
    this.#show(card);
    this.#place();
  };
  #onMove = (event) => {
    if (!this.#card || event.pointerType !== 'mouse') return;
    this.#point = { x: event.clientX, y: event.clientY };
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#place);
  };
  #onOut = (event) => {
    if (!this.#card) return;
    if (this.#card.contains(event.relatedTarget)) return;
    this.#hide();
  };
  #onFocus = (event) => {
    const card = event.target.closest?.('[data-tip-tone]');
    if (!card || !card.matches(':focus-visible')) return;
    const box = card.getBoundingClientRect();
    this.#show(card);
    this.#point = { x: box.left + 24, y: box.top + 24 };
    this.#place();
  };
  #onBlur = () => this.#hide();
  #onScroll = () => { if (this.#card && !this.#card.matches(':hover')) this.#hide(); };

  connectedCallback() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#scope = this.parentElement;
    if (!this.#scope) return;
    this.#panel = document.createElement('div');
    this.#panel.className = 'theme-tip';
    this.#panel.setAttribute('aria-hidden', 'true');
    document.body.append(this.#panel);
    this.#scope.addEventListener('pointerover', this.#onOver, { passive: true });
    this.#scope.addEventListener('pointermove', this.#onMove, { passive: true });
    this.#scope.addEventListener('pointerout', this.#onOut, { passive: true });
    this.#scope.addEventListener('focusin', this.#onFocus);
    this.#scope.addEventListener('focusout', this.#onBlur);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener('pointerover', this.#onOver);
    this.#scope?.removeEventListener('pointermove', this.#onMove);
    this.#scope?.removeEventListener('pointerout', this.#onOut);
    this.#scope?.removeEventListener('focusin', this.#onFocus);
    this.#scope?.removeEventListener('focusout', this.#onBlur);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
    this.#panel?.remove();
  }

  #show(card) {
    const source = card.querySelector('[data-tip]');
    if (!source || !this.#panel) return;
    this.#card = card;
    this.#panel.innerHTML = source.innerHTML;
    this.#panel.dataset.tone = card.dataset.tipTone;
    this.#panel.setAttribute('data-show', '');
  }

  #hide() {
    this.#card = null;
    this.#panel?.removeAttribute('data-show');
  }

  #place = () => {
    this.#frame = 0;
    const panel = this.#panel;
    const point = this.#point;
    if (!panel || !point) return;
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const gap = 28;
    const edge = 12;
    let x = point.x + gap;
    let y = point.y + gap;
    if (x + w > window.innerWidth - edge) x = point.x - w - gap;
    if (y + h > window.innerHeight - edge) y = point.y - h - gap;
    x = Math.max(edge, Math.min(x, window.innerWidth - w - edge));
    y = Math.max(edge, Math.min(y, window.innerHeight - h - edge));
    panel.style.setProperty('--ix', `${Math.round(x)}px`);
    panel.style.setProperty('--iy', `${Math.round(y)}px`);
  };
}

if (!customElements.get('s-theme-tip')) customElements.define('s-theme-tip', SThemeTip);
